import { gql, useFragment } from "@apollo/client";

// Styling
import style from "./footer.module.scss";

// Constants
import {
  bgColorFields,
  imgFields,
  linkFields,
  layoutColSettings,
} from "@/constants/gql-fields";

// Import Components
import BackgroundColor from "@/components/bg-color/bg-color";
import Container from "../container/container";
import LayoutColumn from "../layout-column/layout-column";
import Logo from "@/components/logo/logo";
import MenuWP from "../menu-wp/menu-wp";
import SocialIcons from "@/components/social-icons/social-icons";
import SearchIcon from "@/components/search-icon/search-icon";
import Copyright from "../footer--copyright/footer--copyright";
import PrivacyPolicy from "../footer--privacy-policy/footer--privacy-policy";
import LayoutSearch from "../layout-search/layout-search";
import BottomBar from "../footer--bottom-bar/footer--bottom-bar";

export default function Footer({ entry }) {
  // -- Use Fragments
  const { complete, data } = useFragment({
    fragment: Footer.fragments.entry,
    fragmentName: "FooterFragment",
    from: {
      __typename: "RootQuery",
    },
  });
  if (!complete) {
    return;
  }

  // -- Variables
  const genSet = data.generalSettingsOptionsPage;
  const footerSettings = genSet.generalSettingsACF.footerSettings;
  const settingsOptions = footerSettings.footerOptions;
  const footerContent = genSet.generalSettingsACF.footerContent;
  const footerContentSettings = footerContent.footerContentSettings;
  const footerCols = footerContent.footerColumns;
  let footerColumnsStyle = footerSettings.layoutTypeFooter
    ? "footerColumnsFlex"
    : "footerColumnsGrid";

  // -- Breakpoints
  const customBreakpoints = settingsOptions?.includes("breakpoints")
    ? true
    : false;
  const bpMD =
    customBreakpoints && footerSettings.custom_bp_md
      ? footerSettings.custom_bp_md
      : "800";
  const bpLG =
    customBreakpoints && footerSettings.custom_bp_lg
      ? footerSettings.custom_bp_lg
      : "992";

  return (
    <div className="footerWrapper">
      <footer id="footer" className={style.footer}>
        {settingsOptions?.includes("bgcolor") && (
          <BackgroundColor
            colorSettings={
              footerSettings?.footerBackgroundColor.footerBgcColorSettings
            }
          />
        )}
        <Container wide={settingsOptions?.includes("container--wide")}>
          <div
            className={`${style.footerColumns} ${style[footerColumnsStyle]}`}
          >
            {footerCols.map((col, index) => {
              const colItems = col.columnItems;
              const colCount = index + 1;

              // Layout Column Variables
              var columnAttributes = {
                colHori: col.columnHorizontalOrientation,
                colVert: col.columnVerticalOrientation,
                content: col.horizontalContent ? "horizontal" : "vertical",
              };
              var columnStyleItems = {
                colCount: colCount,
                layoutType: footerSettings.layoutTypeFooter ? "flex" : "grid",
                breakpoints: {
                  md: bpMD,
                  lg: bpLG,
                },
                colFlex: [
                  col.columnSizeFlex,
                  col.columnFlexLgPlus,
                  col.columnFlexMdMinus,
                  col.columnFlexMdToLg,
                ],
                colGrid: [
                  col.columnSizeGrid,
                  col.columnGridLgPlus,
                  col.columnGridMdMinus,
                  col.columnGridMdToLg,
                ],
              };

              return (
                <LayoutColumn
                  columnAttributes={columnAttributes}
                  columnStyleItems={columnStyleItems}
                  location="footer"
                  key={index}
                >
                  {colItems.map((item, index) => {
                    // Must add key={index} to each item for the map - will not affect the component but will thrown an error without it //

                    // Variables
                    const searchMode =
                      genSet.generalSettingsACF.searchSettings.searchMode;

                    switch (item.__typename) {
                      // Logo
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsLogoLayout":
                        return (
                          <Logo
                            key={index}
                            entry={genSet.generalSettingsACF.logo}
                          />
                        );

                      // Nav Menu (WP Menu)
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsFooterNavLayout":
                        return (
                          <MenuWP
                            menu={data.navFooter}
                            navStyle="footerNav"
                            key={index}
                          />
                        );

                      // Social Media Icons
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsSocialMediaIconsLayout":
                        const socialHasSearch = item.includeSearchIcon;
                        return (
                          <div className={style.footerIcons} key={index}>
                            <SocialIcons id={genSet.generalSettingsACF.id} />
                            {socialHasSearch &&
                              !searchMode.includes("dropdown") && (
                                <SearchIcon
                                  entry={
                                    genSet.generalSettingsACF.searchSettings
                                  }
                                />
                              )}
                          </div>
                        );

                      // Copyright Line
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsCopyrightLayout":
                        if (!footerContentSettings.includes("copyright"))
                          return;
                        return (
                          <Copyright key={index} entry={genSet.copyright} />
                        );

                      // Privacy Policy Links
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsPrivacyPolicyLinksLayout":
                        if (!footerContentSettings.includes("privacy")) return;
                        return (
                          <PrivacyPolicy
                            key={index}
                            entry={genSet.privacyPolicy}
                          />
                        );

                      // Custom Text
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsCustomTextLayout":
                        const customText = item.text;
                        return (
                          <div
                            className="footerCustomText"
                            key={index}
                            dangerouslySetInnerHTML={{ __html: customText }}
                          ></div>
                        );

                      // Custom Image
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsCustomImageLayout":
                        const customImage = item.image.node;
                        const imgHasLink = item.hasLink;
                        const imgLink = item.imageLink;
                        const imgMaxWidth = item.maxWidth;
                        let imgStyling = "";
                        if (imgMaxWidth) {
                          imgStyling = {
                            maxWidth: imgMaxWidth ?? "",
                          };
                        }
                        let imgItem = (
                          <img
                            src={customImage.sourceUrl}
                            alt={customImage.altText ?? ""}
                            // style={imgStyling}
                          />
                        );
                        return (
                          <div className="footerCustomImage" key={index}>
                            {imgHasLink && (
                              <Link href={imgLink.url} target={imgLink.target}>
                                {imgItem}
                              </Link>
                            )}
                            {imgItem}
                          </div>
                        );

                      // Newsletter
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsNewsletterLayout":
                        return <p key={index}>Newsletter - Not Set Up</p>;

                      // Search
                      case "GeneralSettingsACFFooterContentFooterColumnsColumnItemsSearchLayout":
                        if (searchMode.includes("dropdown")) return;
                        const searchType = item.searchType;
                        return (
                          <LayoutSearch
                            searchType={searchType}
                            searchIconSettings={
                              genSet.generalSettingsACF.searchSettings
                            }
                            key={index}
                          />
                        );

                      // Default
                      default:
                        return null;
                    }
                  })}
                </LayoutColumn>
              );
            })}
          </div>
        </Container>
      </footer>

      {footerContentSettings.includes("bottom") && (
        <BottomBar
          entry={genSet.bottomBar}
          copyright={
            footerContentSettings.includes("copyright") && genSet.copyright
          }
          privacy={
            footerContentSettings.includes("privacy") && genSet.privacyPolicy
          }
        />
      )}
    </div>
  );
}

Footer.fragments = {
  entry: gql`
    fragment FooterFragment on RootQuery {
      # -- Menu
      navFooter: menu(id: "4", idType: DATABASE_ID) {
        # menu(id: $navID, idType: DATABASE_ID) {
        id
        __typename
        ...WpMenuFragment @unmask(mode: "migrate")
      }
      # -- General Settings
      generalSettingsOptionsPage {
        generalSettingsACF {
          id
          __typename
          # -- Footer Items
          footerSettings {
            __typename
            footerOptions
            layoutTypeFooter
            customBpMdFooter
            customBpLgFooter
            footerBackgroundColor {
              footerBgcColorSettings {
                ${bgColorFields}
              }
            }
          }
          footerContent {
            __typename
            footerContentSettings
            footerColumns {
              ${layoutColSettings}
              columnItems {
                __typename
                ... on GeneralSettingsACFFooterContentFooterColumnsColumnItemsSocialMediaIconsLayout {
                  includeSearchIcon
                }
                ... on GeneralSettingsACFFooterContentFooterColumnsColumnItemsCustomTextLayout {
                  text
                }
                ... on GeneralSettingsACFFooterContentFooterColumnsColumnItemsCustomImageLayout {
                  hasLink
                  maxWidth
                  image {
                    ${imgFields}
                  }
                  imageLink {
                    ${linkFields}
                  }
                }
                ... on GeneralSettingsACFFooterContentFooterColumnsColumnItemsSearchLayout {
                  searchType
                }
              }
            }
          }
          # -- Header Items
          logo {
            id
            __typename
            ...LogoFragment @unmask(mode: "migrate")
          }
          searchSettings {
            id
            __typename
            ...SearchIconFragment @unmask(mode: "migrate")
          }
          ...SocialIconsFragment @unmask(mode: "migrate")
        }
        # -- Footer Content
        copyright: generalSettingsACF {
          id
          __typename
          ...CopyrightFragment @unmask(mode: "migrate")
        }
        privacyPolicy: generalSettingsACF {
          id
          __typename
          ...PrivacyPolicyFragment @unmask(mode: "migrate")
        }
        bottomBar: generalSettingsACF {
          id
          __typename
          ...BottomBarFragment  @unmask(mode: "migrate")
        }
      }
    }
    # Fragment Imports
    ${Logo.fragments.entry}
    ${SearchIcon.fragments.entry}
    ${SocialIcons.fragments.entry}
    ${Copyright.fragments.entry}
    ${PrivacyPolicy.fragments.entry}
    ${BottomBar.fragments.entry}
    ${MenuWP.fragments.menu}
  `,
};
